import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';

interface RotatingHeadlineProps {
  base: string[];
  phrases: string[];
  className?: string;
}

export function RotatingHeadline({ base, phrases, className = '' }: RotatingHeadlineProps) {
  const baseList = Array.isArray(base) ? base : [base];
  const [baseDisplay, setBaseDisplay] = useState(baseList[0]);
  const [phraseText, setPhraseText] = useState(phrases[0]);
  const containerRef = useRef<HTMLHeadingElement>(null);
  const baseRef = useRef<HTMLSpanElement>(null);
  const phraseRef = useRef<HTMLSpanElement>(null);
  const baseProgressRef = useRef({ progress: 0 });

  useGSAP(
    () => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      const typeIn = (value: string) =>
        gsap.fromTo(
          baseProgressRef.current,
          { progress: 0 },
          {
            progress: value.length,
            duration: value.length * 0.07,
            ease: 'none',
            onUpdate: () => {
              const len = Math.round(baseProgressRef.current.progress);
              setBaseDisplay(value.slice(0, len));
            },
          }
        );

      const typeOut = (value: string) =>
        gsap.fromTo(
          baseProgressRef.current,
          { progress: value.length },
          {
            progress: 0,
            duration: value.length * 0.07,
            ease: 'none',
            onUpdate: () => {
              const len = Math.round(baseProgressRef.current.progress);
              setBaseDisplay(value.slice(0, len));
            },
          }
        );

      const baseTl = gsap.timeline({ repeat: -1, repeatDelay: 0 });
      baseList.forEach((value) => {
        baseTl.add(typeIn(value));
        baseTl.to({}, { duration: 10.2 }); // hold
        baseTl.add(typeOut(value));
      });

      const phraseTl = gsap.timeline({ repeat: -1, repeatDelay: 0 });
      phrases.forEach((value) => {
        phraseTl.call(() => setPhraseText(value));
        phraseTl.set(phraseRef.current, { yPercent: 100, autoAlpha: 0 });
        phraseTl.to(phraseRef.current, { yPercent: 0, autoAlpha: 1, duration: 0.6, ease: 'power2.out' });
        phraseTl.to(phraseRef.current, { yPercent: -100, autoAlpha: 0, duration: 0.4, ease: 'power2.in' }, '+=3.6');
      });

      return () => {
        baseTl.kill();
        phraseTl.kill();
      };
    },
    { scope: containerRef }
  );

  return (
    <h1 ref={containerRef} className={`rotating-headline ${className}`}>
      <span className="rotating-headline__line rotating-headline__line--base">
        <span className="rotating-headline__slot-wrapper rotating-headline__slot-wrapper--base">
          <span ref={baseRef} className="rotating-headline__slot-text rotating-headline__slot-text--base">
            {baseDisplay}
          </span>
          <span className="rotating-headline__connector rotating-headline__connector--inside">{' your'}</span>
        </span>
      </span>
      <span className="rotating-headline__line rotating-headline__line--phrase">
        <span className="rotating-headline__slot-wrapper rotating-headline__slot-wrapper--phrase" aria-live="polite" aria-atomic="true">
          <span ref={phraseRef} className="rotating-headline__slot-text rotating-headline__slot-text--phrase">
            {phraseText}
          </span>
        </span>
      </span>
    </h1>
  );
}
